'use client';

import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useIntl } from 'react-intl';
import SUPPORTED_LANGUAGES from '@alltrails/core/constants/supportedLanguages';
import ChevronSm from '@alltrails/denali/icons/ChevronSm';
import useLanguageChangeHandler from '@alltrails/layout/hooks/useLanguageChangeHandler';
import type { LanguageRegionCode } from '@alltrails/types/LanguageRegionCode';
import styles from './MapLanguageDropdown.module.scss';

type MapLanguageDropdownProps = {
  languageRegionCode: LanguageRegionCode;
  pageReloadOnLanguageChange?: boolean;
};

const MapLanguageDropdown = ({ languageRegionCode, pageReloadOnLanguageChange }: MapLanguageDropdownProps) => {
  const intl = useIntl();
  const [selectedLanguage, setSelectedLanguage] = useState(languageRegionCode);
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, right: 0 });
  const dropdownRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const onLanguageChange = useLanguageChangeHandler(pageReloadOnLanguageChange);

  const selectLanguageText = intl.formatMessage({ defaultMessage: 'Select a language' });
  const availableLanguages = SUPPORTED_LANGUAGES;
  const currentLanguage = availableLanguages.find(l => l.languageRegionCode === selectedLanguage);

  useEffect(() => {
    if (isOpen && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.top - 4,
        right: window.innerWidth - rect.right
      });
    }
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const dropdownElement = (dropdownRef.current as any)?.dropdownElement;

      if (triggerRef.current && !triggerRef.current.contains(target) && dropdownElement && !dropdownElement.contains(target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
    return undefined;
  }, [isOpen]);

  const handleLanguageSelect = (newLanguageRegionCode: LanguageRegionCode) => {
    if (selectedLanguage === newLanguageRegionCode) {
      setIsOpen(false);
      return;
    }

    setSelectedLanguage(newLanguageRegionCode);
    onLanguageChange(newLanguageRegionCode);
    setIsOpen(false);
  };

  return (
    <>
      <div ref={dropdownRef} className={styles.container}>
        <button
          ref={triggerRef}
          type="button"
          className={styles.trigger}
          onClick={e => {
            e.stopPropagation();
            setIsOpen(!isOpen);
          }}
          aria-expanded={isOpen}
          aria-haspopup="listbox"
          aria-label={selectLanguageText}
        >
          <span>{currentLanguage?.translatedLanguageName}</span>
          <ChevronSm orientation={isOpen ? 'up' : 'down'} size="sm" className={styles.chevron} />
        </button>
      </div>
      {isOpen &&
        createPortal(
          <div
            ref={node => {
              if (node && dropdownRef.current) {
                (dropdownRef.current as any).dropdownElement = node;
              }
            }}
            className={styles.dropdown}
            role="listbox"
            style={{
              top: `${dropdownPosition.top}px`,
              right: `${dropdownPosition.right}px`,
              transform: 'translateY(-100%)'
            }}
          >
            <button type="button" role="option" aria-selected={false} disabled className={styles.placeholder}>
              {selectLanguageText}
            </button>
            {availableLanguages.map(language => (
              <button
                key={language.languageRegionCode}
                type="button"
                role="option"
                aria-selected={language.languageRegionCode === selectedLanguage}
                className={`${styles.option} ${language.languageRegionCode === selectedLanguage ? styles.selected : ''}`}
                onClick={() => handleLanguageSelect(language.languageRegionCode as LanguageRegionCode)}
              >
                {language.translatedLanguageName}
              </button>
            ))}
          </div>,
          document.body
        )}
    </>
  );
};

export default MapLanguageDropdown;
